<template>
    <div class="col-12 nopads">
        <road-sign-filter @filters-updated="updateFilters"/>
        <div
            v-if="loading"
            id="loader"
            class="spinner"
        />
        <road-sign-bulk-editor-dialog
            v-if="bulkEditMode"
            :road-signs="selectedItems"
            :user="user"
            @closeForm="bulkEditMode = false"
            @closeAndUpdate="updateView"
        />

        <road-sign-wizard
            v-if="editMode"
            :selectedContract="selectedContractIds.length === 1 ? selectedContractIds[0] : null"
            :coordinate="coordinate"
            :road-sign="roadSign"
            :user="user"
            @close="editMode = false; roadSign = null"
            @closeAndUpdate="updateView"
        />

    </div>
</template>

<script>
import {timeUtils} from '../mixins/TimeUtils'
import {mapHelper} from '../mixins/MapMixin'
import {restApi} from '../mixins/RestApiMixin'
import {roadSignHelper} from '@/components/mixins/RoadSignMixin'
import RoadSignFilter from "@/components/roadsign/RoadSignFilter";
import {geometryMixin} from "@/components/mixins/GeometryMixin";
import RoadSignBulkEditorDialog from "./RoadSignBulkEditorDialog.vue";
import RoadSignWizard from "./RoadSignWizard.vue";
import { getDistance } from 'geolib';

// Import all images from roadsign directory
const imageModules = import.meta.glob('@/assets/roadsign/**/*.{svg,png,jpg}', {eager: true});


export default {
    name: 'RoadSignMap',
    components: {RoadSignWizard, RoadSignBulkEditorDialog, RoadSignFilter},
    mixins: [timeUtils, mapHelper, restApi, roadSignHelper, geometryMixin],
    props: {
        map: {
            type: Object,
            default: null
        },
        boundingBox: {
            type: Array,
            default: null
        },
        signs: {
            type: Array,
            default: []
        },
        user: {
            type: Object,
            default: null
        },
        coordinate: {
            type: Object,
            default: null
        },
        infoVisible: {
            type: Boolean,
            default: true
        },
        zoomOnDrawMarker: {
            type: Boolean,
            default: false
        },
        draggable: {
            type: Boolean,
            default: false
        },
        selectedContractIds: {
            type: Array,
            default() {
                return []
            }
        },
        selectedItems: {
            type: Array,
            default() {
                return []
            }
        },
    },
    data: function () {
        return {
            loading: false,
            allSigns: [],
            visibleSigns: [],
            typeEntries: [],
            selectedTypes: [],
            selectedConditions: [],
            status: 1,
            editMode: false,
            bulkEditMode: false,
            listOpen: false,
            allSelected: false,
            indeterminate: false,
            visibilityZoomLevel: 7,
            roadSign: null,
            imageModuleEntries: null,
            signSvgUrlCache: null,
            unknownSignSvgUrl: null,
            zoomToGroup: true
        }
    },
    watch: {
        map() {
            this.$nextTick(function () {
                if (this.map && (this.boundingBox || this.signs)) {
                    this.initView()
                }
            })
        },
        boundingBox: function () {
            if (this.map) {
                this.initView()
            }
        },
        selectedContractIds() {
            if (this.map) {
                this.initView(true)
            }
        },
        selectedTypes(newVal) {
            if (newVal.length === 0) {
                this.indeterminate = false
                this.allSelected = false
            } else if (newVal.length === this.typeEntries.length) {
                this.indeterminate = false
                this.allSelected = true
            } else {
                this.indeterminate = true
                this.allSelected = false
            }
            this.filterByTypes()
        },
        coordinate: function () {
            if (this.coordinate) {
                this.roadSign = null
                this.editMode = true
            } else {
                this.editMode = false
            }
        },
        selectedItems: function () {
            if (this.selectedItems && this.selectedItems.length > 0) {
                this.bulkEditMode = true
            } else {
                this.bulkEditMode = false
            }
        },
        signs() {
            if (this.map) {
                this.hideSigns()
                this.initView()
            }
        }
    },
    mounted() {
        // Init image module lookup for better perf
        this.imageModuleEntries = Object.entries(imageModules)
        this.signSvgUrlCache = new Map()

        if (this.map && (this.boundingBox || this.signs)) {
            this.initView()
        }
        if (this.coordinate) {
            this.editMode = true
        }
    },

    beforeUnmount() {
        this.hideSigns()
    },

    methods: {
        resolveSignSvgUrl(mapIconName) {
            if (!mapIconName) return null
            if (this.signSvgUrlCache.has(mapIconName)) {
                return this.signSvgUrlCache.get(mapIconName)
            }
            let svgUrl = null
            for (const [path, module] of this.imageModuleEntries) {
                if (path.includes(mapIconName)) {
                    svgUrl = module.default || module
                    break
                }
            }
            this.signSvgUrlCache.set(mapIconName, svgUrl)
            return svgUrl
        },

        updateFilters(status, filters) {
            this.status = status
            this.selectedConditions = filters
            this.initView(true)
        },
        updateView() {
            this.hideSigns()
            this.initView(true)
            this.editMode = false
            this.bulkEditMode = false
        },
        initView: function (force) {
            if (!this.map) return
            // Signs we're given
            if (this.signs && this.signs.length > 0) {
                this.visibleSigns = this.signs
                this.drawSigns()
                if (this.zoomToGroup) {
                    this.$nextTick(() => this.map?.zoomToGroup(this.TRAFFIC_SIGN))
                    this.zoomToGroup = false
                }
            } else {
                if (this.map.getMapZoomLevel() < this.visibilityZoomLevel) {
                    this.bbox = null
                    this.hideSigns()
                } else if (this.boundingBox && (force || (!this.bbox || this.isBboxOutsideBbox(this.boundingBox, this.bbox, 0.002)))) {
                    const extendRate = 0.005
                    this.bbox = [
                        this.boundingBox[0] - extendRate,
                        this.boundingBox[1] - extendRate,
                        this.boundingBox[2] + extendRate,
                        this.boundingBox[3] + extendRate
                    ]
                    this.fetchSigns()
                }
            }
        },

        async fetchSigns() {
            let params = {}
            if(this.status === 0) {
                this.status = null
            } else if(!this.status) {
                this.status = 1
            }
            if(this.selectedConditions && this.selectedConditions.length > 0) {
                let conditionValues = []
                this.selectedConditions.forEach(cond => {
                    conditionValues.push(cond.value)
                })
                params.condition = conditionValues
            }
            params.status = this.status
            params.contract = this.selectedContractIds
            params.bbox = this.bbox
            this.loading = true
            this.restFetchParams(this.roadSignMapUrl, params, this.handleSearch)
        },

        handleSearch(response) {
            this.loading = false
            if (response && response.data) {
                this.allSigns = response.data
                this.prepareSigns()
            }
        },

        prepareSigns() {
            if (this.allSigns) {
                this.initTypeEntries()
                this.filterByTypes()
            } else {
                this.visibleSigns = []
            }
        },

        drawSigns () {
            this.visibleSigns.forEach(function (sign) {
                this.drawSign(sign)
            }, this)
        },

        drawSign(sign) {
            const mapIconName = this.getSignIcon(sign.sign)
            if (!mapIconName) return
            const markerStore = this.map.getMarkerStore()
            const svgUrl = this.resolveSignSvgUrl(mapIconName)
            if (!svgUrl) {
                console.warn('Road sign image not found:', mapIconName)
                return
            }
            const icon = markerStore.getSvgMarker(
                svgUrl,
                sign.sign_condition === 10,
                sign.sign === 'C32' ? (sign.value ? sign.value : 60) : null
            )
            this.map.showMapMarker(
                sign.id,
                this.TRAFFIC_SIGN,
                sign.position.y,
                sign.position.x,
                icon
            )
        },

        hideSigns: function () {
            if (this.map) {
                this.map.removeMapItemsByType(this.TRAFFIC_SIGN)
            }
            this.visibleSigns = []
        },

        initTypeEntries() {
            const typeMap = new Map()
            if (this.allSigns && this.allSigns.length > 0) {
                for (const item of this.allSigns) {
                    if (!typeMap.has(item.type_id)) {
                        typeMap.set(item.type_id, {
                            text: item.name,
                            value: item.type_id
                        })
                    }
                }
            }
            this.typeEntries = Array.from(typeMap.values())
            this.sortTypeEntries()
            if (!this.selectedTypes || this.selectedTypes.length < 1) {
                this.toggleAll(true)
            }
        },

        sortTypeEntries: function () {
            this.typeEntries.sort(function (a, b) {
                if (a.text < b.text) {
                    return -1;
                }
                if (a.text > b.text) {
                    return 1;
                }
                return 0;
            });
        },

        filterByTypes() {
            this.hideSigns()
            if (!this.allSigns || this.allSigns.length === 0) {
                this.visibleSigns = []
                return
            }
            if (this.allSelected) {
                this.visibleSigns = this.allSigns
            } else {
                const selectedTypeSet = new Set(this.selectedTypes)
                this.visibleSigns = this.allSigns.filter(item => selectedTypeSet.has(item.type_id))
            }
            this.drawSigns()
        },

        toggleList: function () {
            this.listOpen = !this.listOpen
        },

        toggleAll(checked) {
            this.selectedTypes = []
            if (checked) {
                this.typeEntries.forEach(item => {
                    this.selectedTypes.push(item.value)
                })
            }
        },

        getVisibleSigns() {
            return this.visibleSigns
        },

        editSign(sign) {
            this.loading = true
            this.restFetch(this.roadSignUrl + '/' + sign.id, this.handleSign)
        },

        handleSign(response) {
            this.loading = false
            if (response && response.data) {
                this.roadSign = response.data
                this.editMode = true
            } else {
                // Show error?
            }
        },


        showMarkersBubble: function (data) {
            if (data.type === this.TRAFFIC_SIGN) {
                let item = this.visibleSigns.find(s => s.id === data.id)
                if (item) {
                    // Bubble position
                    const x = item.position.x
                    const y = item.position.y
                    // Resolve signs that are near
                    let hoveredSigns = this.visibleSigns.filter(sign => {
                           const distance =  getDistance({latitude: sign.position.y, longitude: sign.position.x},
                                {latitude: item.position.y,longitude: item.position.x})
                           return distance < 1
                        }
                    )
                    // Show svg's in info bubble in case we have more than one sign under the mouse pointer
                    if (hoveredSigns && hoveredSigns.length > 1) {
                        // Get icons
                        let icons = []
                        hoveredSigns.forEach(sign => {
                            let mapIconName = this.getSignIcon(sign.sign)
                            if (mapIconName) {
                                // Find the matching image module
                                let svgUrl = null
                                for (const [path, module] of Object.entries(imageModules)) {
                                    if (path.includes(mapIconName)) {
                                        svgUrl = module.default || module
                                        break
                                    }
                                }
                                if (svgUrl) {
                                    icons.push(svgUrl)
                                }
                            }
                        })
                        if (icons.length > 0) {
                            this.map.addIconsLabel(item.id, icons, y, x, this.TRAFFIC_SIGN)
                            setTimeout(() => {
                                this.hideMarkersBubble(data)
                            }, 1500)
                        }
                    }
                }
            }
        },

        hideMarkersBubble: function (data) {
            // Hide info bubble
            this.map.hideMarkerLabel(data.id, data.type)
        },

        initZoomToGroup() {
            this.zoomToGroup = true
        }

    }
}
</script>
